import React, { useEffect } from 'react';
import classnames from 'classnames';

import Element from '../../Element/Element';

import styles from './Search.module.scss';

import injectScript from '../../../shared/utils/injectScript/injectScript';
import addAdditionalSearchStyles from '../../../shared/utils/injectScript/addAdditionalSearchStyles';

import './addsearch.scss';
import Icon from '../../Icon/Icon';

export type SearchProps = {
  /** Search page url. Goes there if user hits enter after keyword */
  searchPageUrl: string;
  /** Url for AddSearch script */
  addSearchScriptUrl: string;
  /** Key for AddSearch script (Not needed if included in the AddSearch script url) */
  addSearchScriptKey?: string;
  /** Placeholder text for search input field */
  placeholder: string;
  /** Is search visible */
  isOpen: boolean;
  /** Added to ensure if the additional styles should be added to search */
  addAdditionalSearchStyle: boolean;
  /** Placeholder to add additional style that should be added to search */
  additionalSearchCssClasses?: string;
  /** Toggle search callback */
  onSearchToggle: () => void;
};

const Search: React.FunctionComponent<SearchProps> = ({
  placeholder,
  searchPageUrl,
  addSearchScriptUrl,
  addSearchScriptKey = '',
  isOpen,
  addAdditionalSearchStyle,
  additionalSearchCssClasses,
  onSearchToggle,
}) => {
  const textInput = React.createRef<HTMLInputElement>();

  // AddSearch script injection. Search will not work without it.
  injectScript(`${addSearchScriptUrl}${addSearchScriptKey}`);

  if (addAdditionalSearchStyle) {
    addAdditionalSearchStyles(additionalSearchCssClasses);
  }

  useEffect(() => {
    if (isOpen) {
      textInput.current?.focus();
    }
  }, [isOpen]);

  return (
    <div className={styles.search}>
      <form action={searchPageUrl} method="GET" className={styles.search_form}>
        <div
          data-testid="search-overlay"
          onClick={() => onSearchToggle()}
          className={classnames({
            [styles.search_overlay]: true,
            [styles.search_overlay__visible]: isOpen,
          })}
        />

        <div
          data-testid="search"
          className={classnames({
            [styles.search_inputWrapper]: true,
            [styles.search_inputWrapper__active]: isOpen,
          })}
        >
          <Element
            tagName="button"
            data-testid="search-open-icon"
            className={styles.search_icon}
            onClick={(event: React.MouseEvent) => {
              !isOpen && event.preventDefault();
              onSearchToggle();
            }}
            tabIndex={0}
          >
            <Icon name="search" size="lg" color="black" />
          </Element>

          <input
            data-addsearch-field
            disabled
            ref={textInput}
            autoComplete="off"
            className={classnames(styles.search_input, 'addsearch')}
            type="text"
            placeholder={placeholder}
            tabIndex={isOpen ? 0 : -1}
          />

          <Element
            tagName="button"
            data-testid="search-close-icon"
            className={styles.search_closeIcon}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              onSearchToggle();
            }}
            tabIndex={isOpen ? 0 : -1}
          >
            <Icon name="close" size="lg" color="black" />
          </Element>
        </div>
      </form>
    </div>
  );
};

export default Search;
